/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../../styles/NewApplication.css';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useFormCache from '../../hooks/useFormCache';

axios.defaults.withCredentials = true;

const ResourceOnlyApplication = ({ user, mode = 'new', initialData, applicationId, onSaved, revisionMode = false }) => {
  const navigate = useNavigate();

  const cacheKey = `resource-only-form-${user?.id || 'guest'}-${mode}${applicationId ? `-${applicationId}` : ''}`;
  const { cachedData, updateCache, clearCache, isLoading: isCacheLoading, isSaving } = useFormCache(cacheKey);

  const [isRestoringCache, setIsRestoringCache] = useState(false);
  const [cacheRestored, setCacheRestored] = useState(false);

  // Form state
  const [requestTitle, setRequestTitle] = useState('');
  const [selectedFacility, setSelectedFacility] = useState('');
  const [requestedResources, setRequestedResources] = useState([{ resource: '', quantity: '' }]);
  const [purpose, setPurpose] = useState('');
  const [personnel, setPersonnel] = useState([]);
  const [checkingEmailIndex, setCheckingEmailIndex] = useState(null);

  // Lookup data
  const [facilities, setFacilities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projectRoles, setProjectRoles] = useState([]);
  const [facilityResources, setFacilityResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch lookup data on mount
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [facilitiesRes, departmentsRes, rolesRes] = await Promise.all([
          axios.get('/api/facilities'),
          axios.get('/api/departments'),
          axios.get('/api/project-roles')
        ]);
        setFacilities(facilitiesRes.data);
        setDepartments(departmentsRes.data);
        setProjectRoles(rolesRes.data);
      } catch (err) {
        console.error('Error fetching form data:', err);
        setError('Failed to load facilities');
      } finally {
        setLoading(false);
      }
    };
    fetchLookups();
  }, []);

  const piRoleId = projectRoles.find(r => r.role === 'PI')?._id;

  // New request: start the people list with the requester as PI. They can change
  // their role and add the PI they are requesting for, or others sharing the cost.
  useEffect(() => {
    if (initialData || isCacheLoading || personnel.length > 0 || !user || !piRoleId) return;
    if (cachedData?.personnel?.length > 0) return;
    setPersonnel([{
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      email: user.email || '',
      department: user.department?._id || user.department || '',
      role: piRoleId,
      existingUser: true
    }]);
  }, [initialData, isCacheLoading, cachedData, personnel.length, user, piRoleId]);

  // Restore from cache
  useEffect(() => {
    if (isCacheLoading || cacheRestored || isRestoringCache) return;
    if (initialData) return;
    if (!cachedData || Object.keys(cachedData).length === 0) return;

    setIsRestoringCache(true);
    if (cachedData.requestTitle) setRequestTitle(cachedData.requestTitle);
    if (cachedData.selectedFacility) setSelectedFacility(cachedData.selectedFacility);
    if (cachedData.requestedResources?.length > 0) setRequestedResources(cachedData.requestedResources);
    if (cachedData.purpose) setPurpose(cachedData.purpose);
    if (cachedData.personnel?.length > 0) setPersonnel(cachedData.personnel);
    setCacheRestored(true);
    setIsRestoringCache(false);
  }, [isCacheLoading, cachedData, cacheRestored, isRestoringCache, initialData]);

  // Hydrate from initialData (edit mode)
  useEffect(() => {
    if (!initialData) return;
    setRequestTitle(initialData.project_title || '');
    setSelectedFacility(initialData.facility?._id || initialData.facility || '');
    setPurpose(initialData.resource_request_purpose || '');
    if (initialData.requested_resources?.length > 0) {
      setRequestedResources(initialData.requested_resources.map(r => ({
        resource: r.resource?._id || r.resource || '',
        quantity: r.quantity || '',
        notes: r.notes || ''
      })));
    }
    // Requests made before people could be added have one entry with no role.
    setPersonnel((initialData.users || []).map(u => ({
      firstName: u.first_name || '',
      lastName: u.last_name || '',
      email: u.email || '',
      department: u.customDepartment ? 'not_listed' : (u.department?._id || u.department || ''),
      customDepartment: u.customDepartment || '',
      role: u.role?._id || u.role || '',
      existingUser: u.isExistingUser,
      isDeleted: u.isDeleted || false
    })));
  }, [initialData]);

  // Auto-save to cache
  const saveToCache = useCallback(() => {
    if (isRestoringCache || mode !== 'new') return;
    updateCache({
      requestTitle,
      selectedFacility,
      requestedResources,
      purpose,
      personnel
    });
  }, [requestTitle, selectedFacility, requestedResources, purpose, personnel, updateCache, isRestoringCache, mode]);

  useEffect(() => {
    if (!isCacheLoading && cacheRestored) {
      saveToCache();
    }
  }, [saveToCache, isCacheLoading, cacheRestored]);

  // Fetch resources when facility changes
  useEffect(() => {
    if (!selectedFacility) {
      setFacilityResources([]);
      return;
    }
    const fetchResources = async () => {
      setResourcesLoading(true);
      try {
        const res = await axios.get(`/api/resources?facility=${selectedFacility}`);
        setFacilityResources(res.data);
      } catch (err) {
        console.error('Error fetching resources:', err);
        toast.error('Failed to load resources for this facility');
      } finally {
        setResourcesLoading(false);
      }
    };
    fetchResources();
  }, [selectedFacility]);

  // Resource row handlers
  const handleResourceChange = (index, field, value) => {
    const updated = [...requestedResources];
    updated[index] = { ...updated[index], [field]: value };
    setRequestedResources(updated);
  };

  const addResourceRow = () => {
    setRequestedResources([...requestedResources, { resource: '', quantity: '' }]);
  };

  const removeResourceRow = (index) => {
    if (requestedResources.length === 1) return;
    setRequestedResources(requestedResources.filter((_, i) => i !== index));
  };

  // People handlers (same lookup as the project application form)
  const handlePersonnelChange = async (index, field, value) => {
    const updated = [...personnel];
    updated[index] = { ...updated[index], [field]: value };

    if (field === 'email' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setCheckingEmailIndex(index);
      try {
        const response = await axios.get(`/api/users/check-email/${value}`);
        if (response.data.exists) {
          const found = response.data.user;
          updated[index] = {
            ...updated[index],
            firstName: found.first_name,
            lastName: found.last_name,
            department: found.customDepartment
              ? 'not_listed'
              : (typeof found.department === 'object' ? found.department?._id : found.department) || '',
            customDepartment: found.customDepartment || '',
            existingUser: true
          };
          toast.success(`User ${value} found and details added`);
        } else {
          updated[index] = { ...updated[index], existingUser: false };
        }
      } catch (err) {
        console.error('Error checking email:', err);
      } finally {
        setCheckingEmailIndex(null);
      }
    }

    setPersonnel(updated);
  };

  const addPerson = () => {
    setPersonnel([...personnel, { firstName: '', lastName: '', email: '', department: '', role: '' }]);
  };

  const removePerson = (index) => {
    if (personnel.length === 1) {
      toast.error('Cannot remove the last person. Please add another person first.');
      return;
    }
    const remaining = personnel.filter((_, i) => i !== index);
    if (!remaining.some(p => p.role === piRoleId)) {
      toast.error('Cannot remove this person. At least one PI must remain on the request.');
      return;
    }
    setPersonnel(remaining);
  };

  const getResourceUnit = (resourceId) => {
    const r = facilityResources.find(fr => fr._id === resourceId);
    if (!r?.unit) return '';
    return r.unit.base_unit || r.unit.unit_short || '';
  };

  const getResourceName = (resourceId) => {
    const r = facilityResources.find(fr => fr._id === resourceId);
    return r ? r.resource : '';
  };

  // Validation
  const validateForm = () => {
    if (!selectedFacility) {
      toast.error('Please select a facility');
      return false;
    }
    const validResources = requestedResources.filter(r => r.resource && r.quantity);
    if (validResources.length === 0) {
      toast.error('Please add at least one resource with a quantity');
      return false;
    }
    for (const r of validResources) {
      if (Number(r.quantity) <= 0) {
        toast.error(`Quantity must be greater than 0 for ${getResourceName(r.resource)}`);
        return false;
      }
    }
    for (const [i, p] of personnel.entries()) {
      const label = `Person ${i + 1}`;
      if (!p.firstName?.trim() || !p.lastName?.trim()) {
        toast.error(`${label}: first and last name are required`);
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email || '')) {
        toast.error(`${label}: a valid email address is required`);
        return false;
      }
      if (!p.department || (p.department === 'not_listed' && !p.customDepartment?.trim())) {
        toast.error(`${label}: department is required`);
        return false;
      }
      if (!p.role) {
        toast.error(`${label}: role is required`);
        return false;
      }
    }
    if (!personnel.some(p => p.role === piRoleId)) {
      toast.error('At least one person must be the PI (Principal Investigator)');
      return false;
    }
    return true;
  };

  const clearFormAndCache = () => {
    setRequestTitle('');
    setSelectedFacility('');
    setRequestedResources([{ resource: '', quantity: '' }]);
    setPurpose('');
    setPersonnel([]);
    clearCache();
    setCacheRestored(false);
  };

  const handleClearForm = () => {
    const confirmClear = window.confirm(
      'Are you sure you want to clear all form data?\n\nThis will:\n• Reset all form fields\n• Clear saved progress from browser storage\n• Cannot be undone\n\nClick OK to proceed or Cancel to keep your data.'
    );
    if (confirmClear) {
      clearFormAndCache();
      toast.success('Form cleared successfully!', { autoClose: 3000 });
    }
  };

  const handleSubmit = async (status) => {
    try {
      if ((status === 'Submitted' || revisionMode) && !validateForm()) return;

      const validResources = requestedResources
        .filter(r => r.resource && r.quantity)
        .map(r => ({
          resource: r.resource,
          quantity: Number(r.quantity),
          notes: r.notes || ''
        }));

      const payload = {
        application_type: 'resource_only',
        project_title: requestTitle.trim() || undefined,
        facility: selectedFacility,
        requested_resources: validResources,
        resource_request_purpose: purpose,
        users: personnel.map(p => ({
          first_name: p.firstName,
          last_name: p.lastName,
          email: p.email,
          department: p.department,
          customDepartment: p.department === 'not_listed' ? p.customDepartment : undefined,
          role: p.role || null
        })),
        project_status: status,
        project_start_date: new Date().toISOString().split('T')[0],
        experiment_conditions: [],
        experiment_details: [],
        pots: [],
        funding: []
      };

      let response;
      if (mode === 'edit' && applicationId) {
        response = await axios.put(`/api/applications/${applicationId}`, payload);
      } else {
        response = await axios.post('/api/applications', payload);
      }

      if (typeof onSaved === 'function') {
        toast.success(revisionMode
          ? 'Changes saved. The request stays in Revision Required until an admin reviews it.'
          : (status === 'Saved' ? 'Resource request saved as draft!' : 'Resource request submitted successfully!'));
        onSaved();
        return;
      }

      clearFormAndCache();

      if (status === 'Saved') {
        toast.success('Resource request saved as draft!', { position: 'top-right', autoClose: 2000 });
      } else {
        toast.success('Resource request submitted successfully!', { position: 'top-right', autoClose: 3000 });
      }

      if (status === 'Submitted') {
        navigate('/projects/submitted');
      } else {
        navigate('/projects/saved');
      }
    } catch (err) {
      console.error('Error submitting resource request:', err);
      const errorMsg = err.response?.data?.error || 'Failed to submit resource request';
      toast.error(errorMsg, { position: 'top-right', autoClose: 5000 });
    }
  };

  if (loading) {
    return <div className="new-application-container"><p>Loading...</p></div>;
  }

  return (
    <div className="new-application-container">
      <div className="form-header">
        <h1>Resource Only Request</h1>
        {mode === 'new' && (
          <div className="form-controls">
            <div className="auto-save-indicator">
              {isSaving ? (
                <span className="saving">💾 Saving...</span>
              ) : (
                <span className="saved">✓ Auto-saved</span>
              )}
            </div>
            <button
              type="button"
              className="clear-form-btn"
              onClick={handleClearForm}
              title="Clear all form data"
            >
              🗑️ Clear Form
            </button>
          </div>
        )}
      </div>
      <p className="form-subtitle">
        Request specific resources from a facility without a full project application.
      </p>

      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(revisionMode ? 'Revision Required' : 'Submitted'); }} className="new-application-form">

        {/* Request Title */}
        <h2>Request Details</h2>
        <div className="form-section">
          <div className="form-group">
            <label htmlFor="requestTitle">Request Title <span className="optional-label">(optional)</span></label>
            <input
              type="text"
              id="requestTitle"
              value={requestTitle}
              onChange={(e) => setRequestTitle(e.target.value)}
              placeholder="e.g., Autoclave bags for lab work (auto-generated if left blank)"
            />
          </div>
        </div>

        {/* People */}
        <h2>People on this Request</h2>
        <div className="form-section">
          <p className="form-subtitle" style={{ marginTop: 0 }}>
            Add everyone involved, for example the PI you are requesting for, or others sharing the cost.
            At least one person must be the PI.
          </p>
          <div className="personnel-table">
            <table>
              <thead>
                <tr>
                  <th>* First Name</th>
                  <th>* Last Name</th>
                  <th>* Email</th>
                  <th>* Department</th>
                  <th>* Role</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {personnel.map((person, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="text"
                        value={person.firstName || ''}
                        onChange={(e) => handlePersonnelChange(index, 'firstName', e.target.value)}
                        disabled={person.existingUser || person.isDeleted}
                        required
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={person.lastName || ''}
                        onChange={(e) => handlePersonnelChange(index, 'lastName', e.target.value)}
                        disabled={person.existingUser || person.isDeleted}
                        required
                      />
                    </td>
                    <td>
                      <input
                        type="email"
                        value={person.email || ''}
                        onChange={(e) => handlePersonnelChange(index, 'email', e.target.value)}
                        disabled={person.isDeleted}
                        required
                      />
                      {checkingEmailIndex === index && <div className="loading-indicator">Checking...</div>}
                      {!person.isDeleted && person.existingUser && <span className="user-status existing">✓ Existing User</span>}
                      {!person.isDeleted && person.existingUser === false && <span className="user-status new">New User</span>}
                    </td>
                    <td>
                      <select
                        value={person.department || ''}
                        onChange={(e) => handlePersonnelChange(index, 'department', e.target.value)}
                        required
                      >
                        <option value="">Select department</option>
                        <option value="not_listed">Department not listed</option>
                        {[...departments].sort((a, b) => (a.department_name || '').localeCompare(b.department_name || '')).map((dept) => (
                          <option key={dept._id} value={dept._id}>
                            {dept.department_name} ({dept.department_code})
                          </option>
                        ))}
                      </select>
                      {person.department === 'not_listed' && (
                        <input
                          type="text"
                          placeholder="Enter department name"
                          value={person.customDepartment || ''}
                          onChange={(e) => handlePersonnelChange(index, 'customDepartment', e.target.value)}
                          style={{ width: '100%', marginTop: '5px' }}
                          required
                        />
                      )}
                    </td>
                    <td>
                      <select
                        value={person.role || ''}
                        onChange={(e) => handlePersonnelChange(index, 'role', e.target.value)}
                        required
                      >
                        <option value="">Select Role</option>
                        {[...projectRoles].sort((a, b) => (a.role || '').localeCompare(b.role || '')).map((role) => (
                          <option key={role._id} value={role._id}>{role.role}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button type="button" className="delete-personnel-btn" onClick={() => removePerson(index)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="add-row-btn" onClick={addPerson}>
            + Add Person
          </button>
        </div>

        {/* Facility Selection */}
        <h2>Facility</h2>
        <div className="form-section">
          <div className="form-group">
            <label htmlFor="facility">Select Facility <span className="required">*</span></label>
            <select
              id="facility"
              value={selectedFacility}
              onChange={(e) => {
                setSelectedFacility(e.target.value);
                setRequestedResources([{ resource: '', quantity: '' }]);
              }}
              required
            >
              <option value="">-- Select Facility --</option>
              {facilities.map(f => (
                <option key={f._id} value={f._id}>{f.facility_name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Resources Table */}
        <h2>Requested Resources</h2>
        <div className="form-section">
          {!selectedFacility ? (
            <div className="no-facility-message">
              Please select a facility first to view available resources.
            </div>
          ) : resourcesLoading ? (
            <div className="no-facility-message">Loading resources...</div>
          ) : facilityResources.length === 0 ? (
            <div className="no-facility-message">No resources available for this facility.</div>
          ) : (
            <>
              <div className="experiment-details-table">
                <table>
                  <thead>
                    <tr>
                      <th>Resource <span className="required">*</span></th>
                      <th>Quantity <span className="required">*</span></th>
                      <th>Unit</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requestedResources.map((row, index) => (
                      <tr key={index}>
                        <td>
                          <select
                            value={row.resource}
                            onChange={(e) => handleResourceChange(index, 'resource', e.target.value)}
                            required
                          >
                            <option value="">-- Select Resource --</option>
                            {facilityResources.map(r => (
                              <option key={r._id} value={r._id}>
                                {r.resource}{r.description ? ` - ${r.description}` : ''}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            value={row.quantity}
                            onChange={(e) => handleResourceChange(index, 'quantity', e.target.value)}
                            placeholder="Qty"
                            required
                            style={{ width: '100px' }}
                          />
                        </td>
                        <td>
                          <span className="unit-label">{getResourceUnit(row.resource) || '—'}</span>
                        </td>
                        <td>
                          <input
                            type="text"
                            value={row.notes || ''}
                            onChange={(e) => handleResourceChange(index, 'notes', e.target.value)}
                            placeholder="Optional notes"
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => removeResourceRow(index)}
                            disabled={requestedResources.length === 1}
                            title="Remove resource"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" className="add-row-btn" onClick={addResourceRow}>
                + Add Another Resource
              </button>
            </>
          )}
        </div>

        {/* Purpose / Notes */}
        <h2>Purpose</h2>
        <div className="form-section">
          <div className="form-group">
            <label htmlFor="purpose">
              Describe the purpose of this resource request <span className="optional-label">(optional)</span>
            </label>
            <textarea
              id="purpose"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="Briefly describe why you need these resources..."
              rows={4}
              maxLength={2000}
            />
            <div className="char-count">{purpose.length}/2000</div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          {revisionMode ? (
            <button type="submit">Save Changes</button>
          ) : (
            <>
              <button type="button" onClick={() => handleSubmit('Saved')}>Save Draft</button>
              <button type="submit">Submit Request</button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default ResourceOnlyApplication;
